import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useI18n } from "../i18n/i18n";
import { getArticleBySlug } from "../data/articles";
import { useSeo } from "../hooks/useSeo";

export default function BlogArticle() {
  const { slug } = useParams();
  const { lang, t } = useI18n();
  const a = getArticleBySlug(slug);

  useSeo({
    title: a ? a.title[lang] : (lang === "de" ? "Artikel nicht gefunden" : "Article not found"),
    description: a ? a.excerpt[lang] : undefined,
    image: a ? (a.cover.startsWith("http") ? a.cover : `https://cloud-ship.de${a.cover}`) : undefined,
    path: `/blog/${slug}`,
    type: "article",
    lang,
  });

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [slug]);

  if (!a) {
    return (
      <main data-testid="blog-article-not-found" className="pt-32 pb-24 min-h-[60vh]">
        <div className="max-w-3xl mx-auto px-6">
          <h1 className="font-display text-3xl text-white mb-4">Article not found</h1>
          <Link to="/blog" className="text-sky-400">{t("blog.back")}</Link>
        </div>
      </main>
    );
  }

  const fmt = new Date(a.date).toLocaleDateString(lang === "de" ? "de-DE" : "en-GB", {
    year: "numeric",
    month: "long",
    day: "2-digit",
  });

  return (
    <main data-testid="blog-article-page" className="pt-32 pb-24">
      <article className="max-w-3xl mx-auto px-6 md:px-8">
        <Link
          to="/blog"
          data-testid="blog-article-back"
          className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-sky-400 mb-8"
        >
          <ArrowLeft className="w-4 h-4" />
          {t("blog.back")}
        </Link>

        <div className="flex items-center gap-3 text-xs text-slate-400 mb-5">
          <span className="text-sky-400 font-semibold uppercase tracking-[0.2em] text-[10px]">
            {a.tag[lang]}
          </span>
          <span>·</span>
          <span>{t("blog.published")} {fmt}</span>
          <span>·</span>
          <span>{a.readingTime} {t("blog.reading_time")}</span>
        </div>

        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl text-white leading-tight">
          {a.title[lang]}
        </h1>

        <div className="mt-8 rounded-2xl overflow-hidden border border-slate-800">
          <img src={a.cover} alt="" className="w-full h-[300px] sm:h-[420px] object-cover" />
        </div>

        <p className="mt-10 text-lg text-slate-300 leading-relaxed">{a.excerpt[lang]}</p>

        <div className="cs-divider my-10" />

        <div className="prose-content space-y-6">
          {a.body[lang].map((p, i) => {
            if (p.startsWith("## ")) {
              return (
                <h2
                  key={i}
                  className="font-display text-2xl sm:text-3xl text-white leading-tight pt-4"
                >
                  {p.slice(3)}
                </h2>
              );
            }
            if (p.startsWith("- ")) {
              const items = p
                .split("\n")
                .filter((l) => l.startsWith("- "))
                .map((l) => l.slice(2));
              return (
                <ul key={i} className="list-none space-y-2 pl-1">
                  {items.map((it, j) => (
                    <li
                      key={j}
                      className="flex items-start gap-3 text-base text-slate-300 leading-[1.75]"
                    >
                      <span className="mt-2 h-1.5 w-1.5 rounded-full bg-sky-400 shrink-0" />
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>
              );
            }
            return (
              <p key={i} className="text-base text-slate-300 leading-[1.85]">
                {p}
              </p>
            );
          })}
        </div>

        <div className="mt-16 cs-glass rounded-2xl p-7">
          <p className="cs-overline mb-2">{t("contact.overline")}</p>
          <p className="text-white text-lg font-display">{t("contact.title")}</p>
          <Link to="/#contact" className="cs-btn-primary mt-5 text-sm" data-testid="article-cta">
            {t("nav.cta")}
          </Link>
        </div>
      </article>
    </main>
  );
}
